import { Component, OnInit, OnDestroy, HostListener, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { TvKeyboardService, KeyboardConfig } from '../../../_service/tv-keyboard.service';
import { QWERTY_LAYOUT, NUMERIC_LAYOUT, EMAIL_LAYOUT, KeyboardLayout } from './keyboard-layouts';

/**
 * TvGridKeyboardComponent - On-screen keyboard for TV D-pad navigation
 *
 * Features:
 * - Grid layout navigable via D-pad (UP/DOWN/LEFT/RIGHT)
 * - Enter key activates focused key (types character or executes action)
 * - Escape closes keyboard
 * - Supports multiple layouts (QWERTY, numeric, email)
 *
 * Integration:
 * - Listens to TvKeyboardService.isVisible$ to show/hide
 * - Uses TvKeyboardService.config$ to determine layout
 * - Calls TvKeyboardService methods for character operations
 */
@Component({
  selector: 'app-tv-grid-keyboard',
  templateUrl: './tv-grid-keyboard.component.html',
  styleUrls: ['./tv-grid-keyboard.component.css']
})
export class TvGridKeyboardComponent implements OnInit, OnDestroy {
  /** Whether keyboard is visible */
  isVisible = false;

  /** Current keyboard configuration */
  config: KeyboardConfig | null = null;

  /** Current input value */
  currentValue = '';

  /** Current keyboard layout */
  layout: KeyboardLayout = QWERTY_LAYOUT;

  /** Suggestions list */
  suggestions: string[] = [];

  /** Whether focus is in the suggestions area (true) or keyboard grid (false) */
  focusInSuggestions = false;

  /** Focused suggestion index */
  focusedSuggestionIndex = 0;

  /** Focused row index */
  focusedRow = 0;

  /** Focused column index within the row */
  focusedCol = 0;

  /** Timestamp when keyboard opened — ignore Enter presses for a brief window to prevent accidental key activation */
  private openedAt = 0;

  private subscriptions: Subscription[] = [];

  private boundBackHandler = this.handleBackKey.bind(this);

  constructor(
    private keyboardService: TvKeyboardService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    // Listen for Back/Escape in capture phase so it fires BEFORE
    // TvBackButtonService (which listens on document bubble phase).
    // This ensures the keyboard closes without triggering page navigation.
    document.addEventListener('keydown', this.boundBackHandler, true);

    // Subscribe to visibility
    this.subscriptions.push(
      this.keyboardService.isVisible$.subscribe(visible => {
        this.isVisible = visible;
        if (visible) {
          // Reset focus to first key when keyboard opens
          this.focusedRow = 0;
          this.focusedCol = 0;
          this.focusInSuggestions = false;
          this.focusedSuggestionIndex = 0;
          // Record open time — Enter presses within 400ms are ignored to prevent
          // accidental key activation from the remote's OK button firing multiple events
          this.openedAt = Date.now();
        }
      })
    );

    // Subscribe to config changes
    this.subscriptions.push(
      this.keyboardService.config$.subscribe(config => {
        this.config = config;
        if (config) {
          // Select layout based on input type
          this.layout = this.getLayoutForInputType(config.inputType);
        }
      })
    );

    // Subscribe to value changes
    this.subscriptions.push(
      this.keyboardService.currentValue$.subscribe(value => {
        this.currentValue = value;
      })
    );

    // Subscribe to suggestions
    this.subscriptions.push(
      this.keyboardService.suggestions$.subscribe(suggestions => {
        this.suggestions = suggestions;
        // Reset suggestion focus when list changes
        this.focusedSuggestionIndex = 0;
      })
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.boundBackHandler, true);
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Capture-phase handler for Back/Escape keys.
   * Closes the keyboard and stops the event from reaching TvBackButtonService.
   */
  private handleBackKey(event: KeyboardEvent): void {
    if (!this.isVisible) return;
    const backKeys = ['Escape', 'Backspace', 'XF86Back', 'GoBack', 'Back', 'BrowserBack'];
    if (backKeys.includes(event.key)) {
      console.log('[KB-BACK]', event.key, 'isVisible=', this.isVisible);
      event.preventDefault();
      event.stopImmediatePropagation();
      this.zone.run(() => {
        this.cancel();
      });
    }
  }

  /**
   * Get appropriate layout based on input type
   */
  private getLayoutForInputType(inputType: string): KeyboardLayout {
    switch (inputType) {
      case 'tel':
      case 'number':
        return NUMERIC_LAYOUT;
      case 'email':
        return EMAIL_LAYOUT;
      case 'text':
      default:
        return QWERTY_LAYOUT;
    }
  }

  /**
   * Handle keydown events for D-pad navigation
   */
  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    // Only process keys when keyboard is visible
    if (!this.isVisible) {
      return;
    }

    // Prevent default browser behavior for handled navigation keys.
    // Back/Escape keys are handled separately in handleBackKey() (capture phase).
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }

    switch (event.key) {
      case 'ArrowUp':
        if (this.focusInSuggestions) {
          this.moveSuggestionUp();
        } else {
          this.moveUp();
        }
        break;
      case 'ArrowDown':
        if (this.focusInSuggestions) {
          this.moveSuggestionDown();
        } else {
          this.moveDown();
        }
        break;
      case 'ArrowLeft':
        if (!this.focusInSuggestions) {
          this.moveLeft();
        }
        break;
      case 'ArrowRight':
        if (this.focusInSuggestions) {
          // Move from suggestions back to keyboard grid
          this.focusInSuggestions = false;
        } else {
          this.moveRight();
        }
        break;
      case 'Enter':
        // Ignore Enter presses within 400ms of keyboard opening to prevent
        // the remote OK button's multiple events from typing a character
        if (Date.now() - this.openedAt < 400) {
          break;
        }
        if (this.focusInSuggestions) {
          this.selectSuggestion();
        } else {
          this.activateKey();
        }
        break;
    }
  }

  /**
   * Move focus up one row
   */
  moveUp(): void {
    if (this.focusedRow > 0) {
      this.focusedRow--;
      // Clamp column to valid range for new row
      const maxCol = this.layout[this.focusedRow].length - 1;
      if (this.focusedCol > maxCol) {
        this.focusedCol = maxCol;
      }
    }
  }

  /**
   * Move focus down one row
   */
  moveDown(): void {
    if (this.focusedRow < this.layout.length - 1) {
      this.focusedRow++;
      // Clamp column to valid range for new row
      const maxCol = this.layout[this.focusedRow].length - 1;
      if (this.focusedCol > maxCol) {
        this.focusedCol = maxCol;
      }
    }
  }

  /**
   * Move focus left one column, or into suggestions if at leftmost column
   */
  moveLeft(): void {
    if (this.focusedCol > 0) {
      this.focusedCol--;
    } else if (this.suggestions.length > 0) {
      // At leftmost column — move into suggestions
      this.focusInSuggestions = true;
      this.focusedSuggestionIndex = 0;
    }
  }

  /**
   * Move focus up in suggestions list
   */
  moveSuggestionUp(): void {
    if (this.focusedSuggestionIndex > 0) {
      this.focusedSuggestionIndex--;
    }
  }

  /**
   * Move focus down in suggestions list
   */
  moveSuggestionDown(): void {
    if (this.focusedSuggestionIndex < this.suggestions.length - 1) {
      this.focusedSuggestionIndex++;
    }
  }

  /**
   * Select the currently focused suggestion
   */
  selectSuggestion(): void {
    if (this.suggestions.length > 0 && this.focusedSuggestionIndex < this.suggestions.length) {
      const suggestion = this.suggestions[this.focusedSuggestionIndex];
      this.keyboardService.selectSuggestion(suggestion);
      setTimeout(() => {
        this.keyboardService.restoreFocus();
      }, 100);
    }
  }

  /**
   * Move focus right one column
   */
  moveRight(): void {
    const maxCol = this.layout[this.focusedRow].length - 1;
    if (this.focusedCol < maxCol) {
      this.focusedCol++;
    }
  }

  /**
   * Activate the currently focused key
   * Types the character or executes the action
   */
  activateKey(): void {
    const key = this.layout[this.focusedRow][this.focusedCol];

    if (key.isAction) {
      // Execute action
      switch (key.value) {
        case 'BACKSPACE':
          this.keyboardService.backspace();
          break;
        case 'CLEAR':
          this.keyboardService.clear();
          break;
        case 'DONE':
          this.submit();
          break;
      }
    } else {
      // Type character
      this.keyboardService.appendChar(key.value);
    }
  }

  /**
   * Submit the current value and close keyboard
   */
  submit(): void {
    this.keyboardService.submit();
    // Restore focus to form field after keyboard closes
    setTimeout(() => {
      this.keyboardService.restoreFocus();
    }, 100);
  }

  /**
   * Cancel input and close keyboard
   */
  cancel(): void {
    this.keyboardService.cancel();
    // Restore focus to form field after keyboard closes
    setTimeout(() => {
      this.keyboardService.restoreFocus();
    }, 100);
  }

  /**
   * Check if a key is currently focused
   */
  isFocused(rowIndex: number, colIndex: number): boolean {
    return !this.focusInSuggestions && this.focusedRow === rowIndex && this.focusedCol === colIndex;
  }

  /**
   * Get CSS class for a key based on focus state
   */
  getKeyClass(rowIndex: number, colIndex: number): string {
    const key = this.layout[rowIndex][colIndex];
    const classes = ['keyboard-key'];

    if (this.isFocused(rowIndex, colIndex)) {
      classes.push('tv-focus');
    }

    if (key.isAction) {
      classes.push('action-key');
    }

    if (key.width && key.width > 1) {
      classes.push(`width-${key.width}`);
    }

    return classes.join(' ');
  }
}
